import React, {useState} from 'react';
import styled from 'styled-components';

const ClassContainer = styled.div`
    padding: 2rem 10rem;
    max-width: 1200px;
    margin: 0 auto;

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
        flex-direction: column;
        padding: 2rem 0;
    }
`;

const ClassGrid = styled.div`
    display: grid;
    margin-top: 10px;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 2rem;
`;

const ClassCard = styled.div`
    cursor: pointer;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;

    &:hover {
        transform: translateY(-5px);
    }
`;

const CardImage = styled.img`
    width: 100%;
    height: 300px;
    object-fit: cover;
`;

const ClassInfo = styled.div`
    padding: 1rem;
`;

const ClassTitle = styled.h2`
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
`;

const ClassDescription = styled.p`
    font-size: 0.9rem;
    color: #666;
    line-height: 1.4;
`;

const Modal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background-color: white;
    padding: 2rem 5rem;
    border-radius: 8px;
    max-width: 60%;
    max-height: 80%;
    overflow-y: auto;
    position: relative;

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
        max-width: 80%;
        padding: 2rem;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
    line-height: 1;
`;

const ImageGallery = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    margin-top: 1rem;
`;

const GalleryImage = styled.img`
    max-width: 100%;
    max-height: 600px;
    object-fit: cover;
    border-radius: 4px;

    @media (max-width: 768px) {
        width: 100%;
        max-height: none;
    }
`;

const ClassDetails = styled.div`
    margin-top: 1em;
    white-space: pre-wrap;
    font-size: 1rem;
    line-height: 1.6;
`;

interface ClassItem {
    title: string;
    description: string;
    details: string;
    images: string[];
}

const Class: React.FC = () => {
    const [selectedClass, setSelectedClass] = useState<ClassItem | null>(null);

    const classes: ClassItem[] = [
        {
            title: "취미, 힐링반",
            description: "일상에 꽃을 함께하고 싶은 분들을 위한 초급 난이도의 취미 수업입니다.",
            details: `취미, 힐링반은 일상에 꽃을 함께하고 싶은 분들, 삶의 활력과 꽃이 주는 에너지를 받고 싶으신 분들께 추천 드리는 취미반 수업입니다.
취미반은 '초급반 난이도'로 진행되며, 초급반이 상품 제작에 중점을 둔 반면 취미반은 꽃에 대한 기초를 바탕으로 창의적인 접근을 추구합니다. 꽃을 처음 접하시는 분들, 기존에 초급 과정을 수료하신 분들, 취미나 힐링을 원하시는 분들에게 추천드립니다. 이 수업을 통해 꽃의 아름다움과 에너지를 경험하며, 일상에 풍요로움을 더할 수 있기를 바랍니다.`,
            images: ["hobby_class/1", "hobby_class/2", "hobby_class/3"]
        },
        {
            title: "초급반",
            description: "꽃을 처음 배우시는 분이나 기초를 탄탄하게 배우고 싶으신 분들을 위한 수업입니다.",
            details: `이 수업은 꽃을 처음 배우시는 분이나 이론부터 기초를 탄탄하게 배우고 싶으신 분들, 그리고 꽃집 창업을 생각하시는 분들을 위한 수업입니다. 기본기를 튼튼히 다지는 작품과 실제 판매 가능한 상품을 중심으로 구성된 커리큘럼으로 구성되어 있습니다. 밝고 화사하며 인기 있는 컬러의 꽃을 준비해드립니다. 
기초반 과정에서는 꽃에 대한 이해와 이론을 익히고, 사계절 꽃과 계절 꽃에 대해 배우며, 스파이럴 기법을 활용한 꽃다발 제작과 포장법, 플로럴폼을 사용한 꽃꽂이에 대한 자신감을 키우는 것을 목표로 합니다. 
이 수업은 꽃을 처음 배우시는 분들뿐만 아니라 계절의 아름다움을 경험하고 싶으신 분들, 그리고 이미 배운 내용을 복습하고 점검하고 싶으신 분들 모두에게 추천드립니다.`,
            images: ["beginner_class/1", "beginner_class/2", "beginner_class/3"]
        },
        {
            title: "중급반",
            description: "메이플레르만의 그림같은 색감과 디자인을 배울 수 있는 내추럴 디자인 코스입니다.",
            details: `메이플레르만의 그림같은 색감과 디자인을 배울 수 있는 내추럴 디자인 코스입니다. 
중급반 수업에서는 사계절 꽃과 더불어, 여러분의 꽃에 대한 범위를 넓혀줄 계절 꽃과 수입 꽃 등 다양한 꽃을 준비해드립니다. 
도전적인 색상 조합, 비대칭과 비정형의 내추럴한 디자인을 통해 새로운 꽃 스타일을 익히실 수 있습니다. 
이 수업은 자신만의 스타일과 색감을 찾고자 하는 분들뿐만 아니라, 초급반을 수료하셨거나 꽃 관련 일을 하고 계신 분들께도 추천드립니다.`,
            images: ["intermediate_class/1", "intermediate_class/2", "intermediate_class/3"]
        },
        {
            title: "고급반",
            description: "꽃에 대한 폭넓은 디자인 및 테크닉, 공간 장식을 함께 배우실 수 있는 클래스입니다.",
            details: `이 과정은 꽃에 대한 폭넓은 디자인 및 테크닉, 공간 장식을 함께 배우실 수 있는 클래스입니다. 
대담하고 웅장한 대형 작품부터 세련된 공간 장식까지, 다양한 스타일의 작품을 직접 만들어볼 수 있습니다. 
타 기관에서 초급 및 중급 과정을 이수하신 분들에게 적합한 수업으로 창의적인 플로럴 디자인을 도전하고 싶으신 분, 웨딩 공간 스타일링에 대해 관심 있으신 분, 현재 꽃집을 운영 중인 분들에게 추천드립니다.`,
            images: ["advanced_class/1", "advanced_class/2", "advanced_class/3", "advanced_class/4"]
        },
        {
            title: "공간장식반",
            description: "대형 작품 제작과 다양한 스타일의 공간 디자인을 배우고 싶으신 분들에게 적합한 과정입니다.",
            details: `대형 작품 제작과 다양한 스타일의 공간 디자인을 배우고 싶으신 분들에게 적합한 과정 입니다. 
웨딩 공간부터 다양한 이벤트 장소까지, 창의적인 장식 기법을 익히실 수 있습니다. 
또한 실제 웨딩 견적 작성 방법도 함께 안내해 드립니다. 
웨딩, 공간 스타일링에 관심 있으신 분들에게 추천드립니다.`,
            images: ["spatial_design_class/1", "spatial_design_class/2", "spatial_design_class/3"]
        },
        {
            title: "창업반",
            description: "꽃집, 작업실 등 다양한 형태의 꽃 관련 창업을 고려하시는 분들을 위한 과정입니다.",
            details: `창업반은 꽃을 처음 배우시는 분들보다는 이미 꽃을 배운 경험이 있는 분들에게 적합한 수업입니다. 
창업을 준비하며 자신의 꽃에 대한 점검을 하거나 아이덴티티 및 방향성을 찾고 싶으신 분들, 정규반에서 꽃을 배우셨지만 창업에 대한 정보가 부족하셨던 분들께 추천합니다. 
이 클래스는 꽃집, 작업실 등 다양한 형태의 꽃 관련 창업을 고려하시는 분들을 위한 과정입니다. 기본 정규 과정이 이론과 실습에 중점을 둔다면, 창업반은 실무에 도움이 될 수 있는 다양한 정보를 추가로 제공합니다. 
기초, 중급, 고급 과정이 함께 구성되어 있어 실무에 유용한 핵심 커리큘럼을 중심으로 진행됩니다. 
수업에서는 꽃 관리법, 사입 방법, 작품의 도매가와 판매가 측정, 꽃 시장 리스트 정보(고속 터미널, 양재, 온라인 꽃 시장), 판매에 적합한 꽃 리스트, 웨딩 견적, 사업자 등록 방법, 꽃 시장 계산서 발급 및 세무에 대한 안내를 제공합니다. 
꽃으로 활동할 수 있는 창업의 범위는 다양합니다. 판매를 중심으로 하는 꽃집, 클래스 및 다양한 행사와 작업을 위한 작업실 등, 자신이 원하는 창업 방향성을 구체화하는 데 도움이 되시길 바랍니다.`,
            images: ["startup_class/1", "startup_class/2", "startup_class/3"]
        },
        {
            title: "디자인반",
            description: "자신만의 스타일과 아이덴티티를 찾고 싶으신 분들을 위한 창의적인 디자인 클래스입니다.",
            details: `꽃을 직접 디자인하는 클래스입니다. 기초반 이상의 수업을 수료하신 분들만 참여하실 수 있으며, 난이도는 중급반 레벨 입니다. 
이 수업은 매번 다채로운 주제로 자신의 작품을 디자인 하는 수업으로 그림, 글, 자연 등에 영감을 받아서 꽃을 디자인 합니다. 
세상의 무용한 것들 속에서 발견하는 아름다움에 영감을 받아 창의적인 꽃 작품을 만듭니다. 데몬 작품이 진행되지만, 저의 스타일을 배우기보다는 스스로 능동적으로 디자인할 수 있는 경험을 제공하는 것이 목표입니다. 
자신만의 스타일과 아이덴티티를 찾고 싶으신 분이나 힐링 클래스를 필요로 하시는 분, 플로리스트 또는 예술 관련 일을 하시는 분들에게도 다채로운 영감을 줄 수 있는 과정입니다.`,
            images: ["design_class/1", "design_class/2", "design_class/3"]
        },
        {
            title: "Oneday Class",
            description: "짧은 시간 동안 집중적으로 꽃을 배울 수 있는 원데이 클래스입니다.",
            details: `Oneday Class는 짧은 시간 동안 집중적으로 꽃을 배우고 싶은 분들을 위한 클래스입니다. 
특정 주제나 기법에 초점을 맞춰 진행되며, 꽃에 대한 기본적인 이해부터 실제 작품 제작까지 경험할 수 있습니다. 
초보자부터 경험자까지 누구나 참여할 수 있으며, 계절별 특별 클래스나 특정 이벤트에 맞춘 클래스 등 다양한 주제로 진행됩니다. 
꽃에 대한 관심은 있지만 정규 클래스에 참여하기 어려운 분들, 특별한 날을 위해 직접 꽃 장식을 만들고 싶은 분들, 또는 꽃 관련 취미를 시작해보고 싶은 분들에게 적합합니다.`,
            images: ["oneday_class/1", "oneday_class/2", "oneday_class/3"]
        },
        {
            title: "1:1 Class",
            description: "개인 맞춤형 1:1 클래스로, 본인의 수준과 요구에 맞게 진행됩니다.",
            details: `1:1 Class는 개인의 요구사항과 수준에 맞춰 진행되는 맞춤형 클래스입니다. 
학습자의 목표, 경험, 관심사에 따라 커리큘럼을 조정하여 최적화된 학습 경험을 제공합니다. 
초보자부터 전문가까지 모든 수준의 학습자에게 적합하며, 특정 기술을 집중적으로 향상시키거나 개인 프로젝트를 위한 지도를 받을 수 있습니다. 
또한, 꽃집 창업을 준비하는 분들이나 특별한 이벤트를 위한 플라워 디자인을 배우고 싶은 분들에게도 이상적입니다. 
1:1 수업을 통해 더욱 빠른 실력 향상과 깊이 있는 학습이 가능하며, 자유로운 일정 조율로 바쁜 현대인들에게 적합한 클래스입니다.`,
            images: ["1on1_class/1", "1on1_class/2", "1on1_class/3"]
        }
    ]

    const openModal = (classItem: ClassItem) => {
        setSelectedClass(classItem);
    };

    const closeModal = () => {
        setSelectedClass(null);
    };

    return (
        <ClassContainer>
            <h2>Class</h2>
            <ClassGrid>
                {classes.map((classItem, index) => (
                    <ClassCard key={index} onClick={() => openModal(classItem)}>
                        <CardImage
                            src={`${process.env.PUBLIC_URL}/class/${classItem.images[0]}.JPG`}
                            alt={classItem.title}
                        />
                        <ClassInfo>
                            <ClassTitle>{classItem.title}</ClassTitle>
                            <ClassDescription>{classItem.description}</ClassDescription>
                        </ClassInfo>
                    </ClassCard>
                ))}
            </ClassGrid>
            {selectedClass && (
                <Modal onClick={closeModal}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <CloseButton onClick={closeModal}>&times;</CloseButton>
                        <h2>{selectedClass.title}</h2>
                        <ClassDetails>{selectedClass.details}</ClassDetails>
                        <ImageGallery>
                            {selectedClass.images.map((image, index) => (
                                <GalleryImage key={index} src={`${process.env.PUBLIC_URL}/class/${image}.JPG`}
                                              alt={`${selectedClass.title} 이미지 ${index + 1}`}/>
                            ))}
                        </ImageGallery>
                    </ModalContent>
                </Modal>
            )}
        </ClassContainer>
    );
};

export default Class;